// src/components/CompanySelector/CompanySelector.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useCompany } from '../../context/CompanyContext';
import { Icon } from '@iconify/react';
import './CompanySelector.css';

const CompanySelector = () => {
  const { companies, selectedCompany, selectCompany } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const btnRef = useRef(null);

  // Calculate dropdown position every time it opens
  useEffect(() => {
    if (isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      const viewportWidth = window.innerWidth;

      // Align to right edge of button; if that goes off-screen, align to left
      let left = rect.right - dropdownWidth;
      if (left < 8) left = 8;
      if (left + dropdownWidth > viewportWidth - 8) {
        left = viewportWidth - dropdownWidth - 8;
      }

      setDropdownStyle({
        top: rect.bottom + 8,
        left,
        width: Math.min(dropdownWidth, viewportWidth - 16),
      });
    }
  }, [isOpen]);

  // Close on scroll or resize (position would be stale)
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  const handleSelectCompany = (company) => {
    selectCompany(company);
    setIsOpen(false);
  };

  return (
    <div className="company-selector">
      <button
        ref={btnRef}
        className="company-selector-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
      >
        <Icon icon="mdi:office-building" className="icon" />
        <span className="company-name">
          {selectedCompany ? selectedCompany.name : 'Select Company'}
        </span>
        <Icon icon="iconoir:nav-arrow-down" className="arrow-icon" />
      </button>

      {isOpen && (
        <>
          {/* Transparent overlay to catch outside clicks */}
          <div
            className="company-selector-overlay"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown — rendered with fixed position calculated from button rect */}
          <div
            className="company-selector-dropdown"
            style={dropdownStyle}
          >
            <div className="dropdown-header">
              <h6>Select Company</h6>
              <button onClick={() => setIsOpen(false)} type="button">
                <Icon icon="radix-icons:cross-2" />
              </button>
            </div>

            <div className="dropdown-body">
              {companies.length === 0 ? (
                <p className="no-companies">No companies available</p>
              ) : (
                <ul className="company-list">
                  {companies.map((company) => (
                    <li
                      key={company.id}
                      className={`company-item ${
                        selectedCompany?.id === company.id ? 'active' : ''
                      }`}
                      onClick={() => handleSelectCompany(company)}
                    >
                      <div className="company-icon">
                        {company.logo ? (
                          <img src={company.logo} alt={company.name} />
                        ) : (
                          <Icon icon="mdi:office-building" />
                        )}
                      </div>
                      <div className="company-info">
                        <h6>{company.name}</h6>
                        <span>{company.code}</span>
                      </div>
                      {selectedCompany?.id === company.id && (
                        <Icon icon="mdi:check" className="check-icon" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySelector;